// packages/utils/src/extractApiError.ts

/**
 * A single field-level validation failure as emitted by the backend
 * AllExceptionsFilter for VALIDATION_FAILED (422).
 */
export type ApiFieldError = {
  property: string;
  constraints?: Record<string, string>;
};

/**
 * Shape of an API error after narrowing.
 * All fields optional because the underlying shape can't be guaranteed.
 *
 * The backend AllExceptionsFilter returns `{ code, message, errors?, details?, timestamp }`
 * with `code` at the ROOT (not nested under `error`), `errors` as the field-level list
 * for class-validator failures, and `details` reserved for constraint metadata.
 */
export type ApiError = {
  status?: number;
  code?: string;
  message?: string;
  errors?: ApiFieldError[];
  details?: unknown;
};

/**
 * Narrow an unknown error (likely an AxiosError) into our API error shape.
 * Never throws — returns an empty object if the shape doesn't match.
 * Used by onError callbacks across the app to avoid `any`.
 */
export function extractApiError(err: unknown): ApiError {
  if (typeof err !== 'object' || err === null) return {};
  const maybe = err as { response?: { status?: number; data?: ApiError } };
  return {
    status: maybe.response?.status,
    ...(maybe.response?.data ?? {}),
  };
}

/**
 * Flatten the backend `errors[]` validation list into `[property, firstConstraintMessage]`
 * pairs, ready to feed react-hook-form's `setError`. Skips entries without a usable message.
 */
export function fieldErrorsFromApi(errors: ApiFieldError[] | undefined): Array<[string, string]> {
  if (!Array.isArray(errors)) return [];
  const pairs: Array<[string, string]> = [];
  for (const entry of errors) {
    if (!entry || typeof entry.property !== 'string' || entry.property.length === 0) continue;
    const messages = entry.constraints ? Object.values(entry.constraints) : [];
    const message = messages.find((m) => typeof m === 'string' && m.length > 0);
    if (message) pairs.push([entry.property, message]);
  }
  return pairs;
}
